<template>
    <div
        ref="dropzone"
        class="relative"
    >
        <input
            type="file"
            ref="input"
            :multiple="maxFiles > 1"
            :disabled="disabled"
            class="absolute top-0 left-0 w-px h-px opacity-0"
        >

        <slot
            :is-loading="is_loading"
            :removeFile="removeFile"
            :reset="reset"
            :open="open"
        />

        <div
            ref="clickable"
            class="hidden"
        />

        <error-modal
            v-if="error && error.response && $error.is(error, { exclude: [400, 422] })"
            :error="error"
            @close="error = null"
        />

        <modal-error
            v-else-if="error"
            :is-open="!!error"
            @close="error = null"
        >
            <template slot="heading">Upload Error</template>

            <p>{{ error.message }}</p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        class="w-full text-center btn btn-red-lightest md:w-auto"
                        @click.prevent="error = null"
                    >
                        Close
                    </button>
                </div>
            </template>
        </modal-error>
    </div>
</template>

<script>
    import _ from 'lodash'
    import axios from 'axios'
    import Dropzone from 'dropzone'

    Dropzone.autoDiscover = false

    export default {
        props: {
            value: {
                type: Array,
                required: true
            },
            url: {
                type: String,
                required: true
            },
            name: {
                type: String,
                default: 'upload'
            },
            maxFiles: {
                type: Number,
                default: null
            },
            maxFilesize: {
                type: Number,
                default: 20 // MB
            },
            acceptedFiles: {
                type: String,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data () {
            return {
                is_loading: false,
                id: 1,
                uploads: [],
                error: null
            }
        },
        mounted () {
            this.$nextTick(() => {
                this.dropzone = new Dropzone(this.$refs.dropzone, {
                    clickable: this.$refs.clickable,
                    url: this.url,
                    maxFiles: this.maxFiles,
                    maxFilesize: this.maxFilesize, // MB
                    acceptedFiles: this.acceptedFiles,
                    paramName: this.name,
                    headers: _.pick(axios.defaults.headers.common, [
                        'Authorization',
                        'X-PERSONA-TOKEN'
                    ]),
                    previewsContainer: false,
                    dictFileTooBig: this.$t('validation.filesize', {
                        attribute: this.name, max: '{{maxFilesize}}'
                    }),
                    dictInvalidFileType: this.$t('validation.mimes', {
                        attribute: this.name, values: this.acceptedFiles
                    })
                })

                this.dropzone.on('addedfile', this.addFile)

                this.dropzone.on('sending', this.updateFile)

                this.dropzone.on('success', this.updateFile)

                this.dropzone.on('error', (upload, error, response) => {
                    error = new Error(_.isString(error)
                        ? error
                        : _.get(error, 'errors.upload.0', 'Unexpected error.')
                    )

                    error.response = response

                    this.error = error

                    this.$emit('error', error)

                    this.updateFile(upload)
                })

                this.dropzone.on('removedfile', (upload) => {
                    upload.status = 'removed'

                    this.updateFile(upload)
                })
            })

            this.$once('hook:beforeDestroy', () => {
                if (this.dropzone) this.dropzone.destroy()
            })

            _.each(this.value, (upload) => {
                _.extend(upload, {
                    status: 'success',
                    type: upload.mime_type
                })

                this.dropzone.emit('addedfile', upload)

                this.dropzone.emit('complete', upload)
            })
        },
        methods: {
            refresh () {
                _.remove(this.uploads, {
                    status: 'removed'
                })

                this.$forceUpdate()

                this.emitChanges()

                this.refreshLoadingStatus()
            },
            open () {
                this.dropzone.hiddenFileInput.click()
            },
            addFile (upload) {
                if (this.maxFiles === 1) {
                    _.each(this.uploads, this.removeFile)
                }

                upload.id = this.id++

                this.uploads.push(upload)

                this.refresh()
            },
            updateFile (upload) {
                this.uploads[this.findFileIndex(upload)] = upload

                this.refresh()
            },
            findFileIndex (upload) {
                return _.findIndex(this.uploads, {
                    id: upload.id
                })
            },
            removeFile (upload) {
                this.dropzone.removeFile(upload)
            },
            emitChanges () {
                this.$emit(
                    'input',
                    _.filter(this.uploads, upload => upload.upload)
                )
            },
            refreshLoadingStatus () {
                this.is_loading = _.filter(this.uploads, {
                    status: 'uploading'
                }).length > 0
            },
            addFilesViaInput () {
                _.each(this.$refs.input.uploads, (upload) => {
                    this.dropzone.addFile(upload)
                })

                this.$refs.input.value = ''
                this.$refs.input.type = 'text'
                this.$refs.input.type = 'file'
            },
            reset () {
                this.dropzone.removeAllFiles()
            }
        }
    }
</script>
